import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import AppHeader from "../components/AppHeader";
import { Field } from "../components/ui";
import { Lock, Unlock, CalendarOff } from "lucide-react";

export default function BlockedSlots() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [allowed, setAllowed] = useState([]);
  const [booked, setBooked] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [reason, setReason] = useState("");
  const [busySlot, setBusySlot] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/meetings/slots?date=${date}`);
      setAllowed(r.allowed || []);
      setBooked(r.booked || []);
      setBlocked(r.blocked || []);
      setError("");
    } catch (e) {
      setError(e?.message || "Could not load slots");
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (time) => {
    const willBlock = !blocked.includes(time);
    setBusySlot(time);
    try {
      if (willBlock) {
        await api.post(`/admin/blocked-slots`, { date, time, reason });
        setBlocked((prev) => [...prev, time]);
      } else {
        await api.delete(`/admin/blocked-slots/${date}/${time}`);
        setBlocked((prev) => prev.filter((t) => t !== time));
      }
    } catch (e) {
      setError(e?.message || "Could not update slot");
    } finally {
      setBusySlot("");
    }
  };

  const allBlocked = allowed.length > 0 && allowed.every((s) => blocked.includes(s));

  const toggleWholeDay = async () => {
    setBusySlot("day");
    try {
      if (allBlocked) {
        await api.delete(`/admin/blocked-slots/day/${date}`);
        setBlocked([]);
      } else {
        const r = await api.post(`/admin/blocked-slots/day`, { date, reason });
        setBlocked(r.blocked || allowed);
      }
    } catch (e) {
      setError(e?.message || "Could not update day");
    } finally {
      setBusySlot("");
    }
  };

  return (
    <>
      <AppHeader title="Blocked Slots" back />
      <div className="sg-main sg-fade-in">
        <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 0, marginBottom: 14 }}>
          Block individual slots when the team is unavailable. Customers won&apos;t be able to book a blocked slot via the public scheduler.
        </p>

        <Field
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          min={today}
          data-testid="blocked-date"
        />

        <Field
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Team offsite, public holiday"
          data-testid="blocked-reason"
        />

        <label className="sg-label" style={{ marginTop: 12 }}>Slots for {date}</label>

        {allowed.length === 0 ? (
          <div className="sg-card" style={{ color: "var(--muted)", fontSize: 13, textAlign: "center" }}>
            We&apos;re closed on this day (Sat/Sun). Nothing to block.
          </div>
        ) : (
          <>
            <button
              onClick={toggleWholeDay}
              disabled={busySlot === "day"}
              data-testid="blocked-toggle-day"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 14px",
                background: allBlocked ? "var(--brand)" : "var(--surface)",
                color: allBlocked ? "#fff" : "var(--brand)",
                border: `1px solid var(--brand)`,
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                marginBottom: 12,
              }}
            >
              <CalendarOff size={16} />
              {busySlot === "day"
                ? "Updating..."
                : allBlocked
                ? "Unblock whole day"
                : "Block whole day"}
            </button>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
              {allowed.map((slot) => {
              const isBooked = booked.includes(slot);
              const isBlocked = blocked.includes(slot);
              const busy = busySlot === slot;
              return (
                <button
                  key={slot}
                  onClick={() => !isBooked && !busy && toggle(slot)}
                  disabled={isBooked || busy}
                  data-testid={`blocked-slot-${slot}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    padding: "12px 14px",
                    background: isBlocked ? "var(--surface-subtle)" : "var(--surface)",
                    border: `1px solid ${isBlocked ? "var(--brand)" : "var(--line)"}`,
                    borderRadius: 12,
                    cursor: isBooked ? "not-allowed" : "pointer",
                    opacity: isBooked ? 0.55 : 1,
                    textAlign: "left",
                    minWidth: 0,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                    {isBlocked ? <Lock size={14} color="var(--brand)" /> : <Unlock size={14} color="var(--muted)" />}
                    <span style={{ fontSize: 15, fontWeight: 800 }}>{slot}</span>
                  </div>
                  <span style={{ fontSize: 11, color: "var(--muted)", marginTop: 4, fontWeight: 600 }}>
                    {isBooked ? "Already booked" : isBlocked ? "Blocked — tap to unblock" : "Available — tap to block"}
                  </span>
                </button>
              );
            })}
            </div>
          </>
        )}

        {error && (
          <div style={{ color: "var(--danger)", fontSize: 13, marginTop: 12 }}>{error}</div>
        )}
      </div>
    </>
  );
}
